import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { JwtService } from '@app/core/auth';
import { distinctUntilChanged } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@app/services/language.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="navbar-root" role="navigation" aria-label="Main navigation">
      <div class="navbar-inner">
        <!-- Left: Brand + tabs -->
        <div class="navbar-left">
          <!-- Brand mark -->
          <a routerLink="/sites" class="brand-mark" aria-label="Home">
            <div class="brand-icon">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <span class="brand-text">{{ 'SIDEBAR.TITLE' | translate }}</span>
          </a>

          <!-- Divider -->
          <div class="nav-divider nav-divider-hide-mobile"></div>

          <!-- Tabs -->
          <nav class="nav-tabs" aria-label="Page navigation">
            <a
              routerLink="/sites"
              routerLinkActive="active-tab"
              [routerLinkActiveOptions]="{ exact: true }"
              class="tab-link"
              [attr.aria-current]="null"
            >
              <svg class="tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
              <span class="tab-label">{{ 'SIDEBAR.SITES' | translate }}</span>
            </a>
            <a
              routerLink="/clients-lies"
              routerLinkActive="active-tab"
              [routerLinkActiveOptions]="{ exact: true }"
              class="tab-link"
              [attr.aria-current]="null"
            >
              <svg class="tab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
              <span class="tab-label">{{ 'SIDEBAR.CLIENTS_LIES' | translate }}</span>
            </a>
          </nav>
        </div>

        <!-- Right: Lang + User -->
        <div class="navbar-right">
          <select
            (change)="switchLang($any($event.target).value)"
            [value]="currentLang"
            class="lang-select"
            aria-label="Language"
          >
            <option value="fr">FR</option>
            <option value="en">EN</option>
          </select>

          <div class="nav-divider" [style.display]="user.name ? '' : 'none'"></div>
            <div class="user-pill" [style.display]="user.name ? 'flex' : 'none'">
              <div class="avatar" [attr.aria-label]="'User: ' + user.name">
                <span>{{ getInitials(user.name) }}</span>
              </div>
              <div class="user-info">
                <p class="user-name">{{ user.name }}</p>
                <p class="user-org">{{ user.organisation }}</p>
              </div>
            </div>
        </div>
      </div>
    </nav>
  `,
  styles: [`
    .navbar-root {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 40;
      background: rgba(255, 255, 255, 0.82);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    .navbar-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 0.75rem;
      height: 56px;
      overflow: visible;
      min-width: 0;
    }
    @media (min-width: 480px) {
      .navbar-inner { padding: 0 1rem; }
    }
    @media (min-width: 640px) {
      .navbar-inner { padding: 0 1.5rem; }
    }

    /* ── Left group ── */
    .navbar-left {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
      overflow: hidden;
    }
    @media (min-width: 480px) {
      .navbar-left { gap: 0.75rem; }
    }
    @media (min-width: 640px) {
      .navbar-left { gap: 1.25rem; }
    }

    /* ── Right group ── */
    .navbar-right {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      flex-shrink: 0;
    }
    @media (min-width: 480px) {
      .navbar-right { gap: 0.75rem; }
    }

    /* ── Brand ── */
    .brand-mark {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-decoration: none;
      flex-shrink: 0;
      border-radius: 8px;
      padding: 0.2rem;
      transition: opacity 200ms;
    }
    .brand-mark:hover { opacity: 0.85; }
    .brand-mark:focus-visible {
      outline: 2px solid #6366f1;
      outline-offset: 2px;
    }
    .brand-icon {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: linear-gradient(135deg, #6366f1, #a855f7);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 12px rgba(99, 102, 241, 0.2);
      flex-shrink: 0;
    }
    .brand-text {
      font-size: 0.85rem;
      font-weight: 700;
      color: #111827;
      letter-spacing: -0.02em;
      white-space: nowrap;
    }
    /* Hide brand text on very small screens */
    @media (max-width: 479px) {
      .brand-text { display: none; }
    }

    /* ── Divider ── */
    .nav-divider {
      width: 1px;
      height: 24px;
      background: rgba(0, 0, 0, 0.08);
      flex-shrink: 0;
    }
    @media (max-width: 479px) {
      .nav-divider-hide-mobile { display: none; }
    }

    /* ── Tabs ── */
    .nav-tabs {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    @media (min-width: 480px) {
      .nav-tabs { gap: 0.35rem; }
    }
    .tab-link {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      padding: 0.4rem 0.5rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #9ca3af;
      border-radius: 8px;
      position: relative;
      transition: color 200ms, background 200ms;
      text-decoration: none;
      white-space: nowrap;
    }
    @media (min-width: 480px) {
      .tab-link { padding: 0.45rem 0.7rem; }
    }
    @media (min-width: 640px) {
      .tab-link { padding: 0.45rem 0.85rem; gap: 0.4rem; }
    }
    .tab-icon {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
    }
    .tab-label {
      display: none;
    }
    @media (min-width: 480px) {
      .tab-label { display: inline; }
    }
    .tab-link:hover {
      color: #374151;
      background: rgba(0, 0, 0, 0.04);
    }
    .tab-link:focus-visible {
      outline: 2px solid #6366f1;
      outline-offset: -2px;
      border-radius: 8px;
    }
    .active-tab {
      color: #4f46e5 !important;
      background: rgba(99, 102, 241, 0.08) !important;
    }
    .active-tab::after {
      content: '';
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 24px;
      height: 2px;
      border-radius: 1px;
      background: linear-gradient(90deg, #6366f1, #a855f7);
      box-shadow: 0 0 6px rgba(99, 102, 241, 0.3);
    }

    /* ── Language select ── */
    .lang-select {
      appearance: none;
      background: rgba(0, 0, 0, 0.03);
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 8px;
      padding: 0.3rem 0.5rem;
      font-size: 0.7rem;
      font-weight: 600;
      color: #6b7280;
      cursor: pointer;
      transition: border-color 200ms, background 200ms;
    }
    @media (min-width: 480px) {
      .lang-select { padding: 0.3rem 0.6rem; font-size: 0.75rem; }
    }
    .lang-select option {
      background: #ffffff;
      color: #111827;
    }
    .lang-select:hover {
      border-color: rgba(0, 0, 0, 0.15);
      background: rgba(0, 0, 0, 0.05);
    }
    .lang-select:focus-visible {
      outline: none;
      border-color: #6366f1;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
    }

    /* ── User pill ── */
    .user-pill {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.3rem 0.5rem;
      border-radius: 10px;
      transition: background 200ms;
      cursor: default;
      flex-shrink: 0;
    }
    .user-pill:hover {
      background: rgba(0, 0, 0, 0.03);
    }
    .avatar {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      background: linear-gradient(135deg, #6366f1, #a855f7);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      box-shadow:
        0 0 0 2px rgba(255, 255, 255, 0.9),
        0 0 0 3px rgba(99, 102, 241, 0.2);
    }
    .avatar span {
      font-size: 0.7rem;
      font-weight: 700;
      color: #fff;
      letter-spacing: 0.03em;
    }
    .user-info {
      display: none;
    }
    @media (min-width: 768px) {
      .user-info { display: block; }
    }
    .user-name {
      font-size: 0.8125rem;
      font-weight: 600;
      color: #111827;
      line-height: 1.2;
    }
    .user-org {
      font-size: 0.7rem;
      color: #9ca3af;
      line-height: 1.2;
    }
  `]
})
export class NavbarComponent implements OnInit {
  private jwtService = inject(JwtService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private languageService = inject(LanguageService);

  user = { name: '', organisation: '' };
  currentLang = 'fr';

  ngOnInit() {
    this.jwtService.token$.pipe(
      distinctUntilChanged((prev, curr) => prev?.sub === curr?.sub),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(decodedToken => {
      if (decodedToken?.name && decodedToken?.sub) {
        this.user.name = decodedToken.name;
        this.user.organisation = decodedToken.organization ?? '';
      } else {
        this.user.name = '';
        this.user.organisation = '';
      }
      this.cdr.markForCheck();
    });

    this.languageService.currentLanguage$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(lang => {
      this.currentLang = lang;
      this.cdr.markForCheck();
    });

    setTimeout(() => this.jwtService.updateTokenObservable(), 100);
  }

  getInitials(name: string): string {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  switchLang(lang: string) {
    this.languageService.setLanguage(lang);
  }
}
